import Konva from 'konva';
import { RefObject } from 'react';
import {
  animationFrameScheduler,
  asyncScheduler,
  combineLatest,
  interval,
  Subject,
} from 'rxjs';
import {
  distinctUntilChanged,
  sample,
  takeUntil,
  tap,
  throttleTime,
} from 'rxjs/operators';

import { MapRenderOperator, RendererOutput } from './interfaces';
import { BackgroundBlurPropTypes } from './BackgroundBlurPropTypes';
import { BackgroundBlurSlice } from '@common/state/modules/mediaDetails/store.models';
import { observeElementWidth } from '../../observeElementWidth';

export const renderer = (
  { current: container }: RefObject<HTMLDivElement>,
  mapRender: MapRenderOperator
): RendererOutput => {
  if (!container) {
    return {
      render: () => {},
      destroy: () => {},
    };
  }
  // Setup Konva
  const stage = new Konva.Stage({
    container,
    width: container.clientWidth,
    height: 32,
  });
  const layer = new Konva.Layer({ listening: false });
  stage.add(layer);

  // Setup Subjects
  const onStartWindowMs$ = new Subject<number>();
  const onStopWindowMs$ = new Subject<number>();
  const onList$ = new Subject<BackgroundBlurPropTypes['list']>();
  const onTimeRange$ = new Subject<BackgroundBlurSlice>();
  const onHeight$ = new Subject<number>();
  const raf$ = interval(undefined, animationFrameScheduler);
  const onDestroy$ = new Subject<boolean>();
  const onResize$ = observeElementWidth(container).pipe(takeUntil(onDestroy$));

  // Setup Observables
  const startWindowMs$ = onStartWindowMs$.pipe(
    distinctUntilChanged(),
    throttleTime(50, asyncScheduler, { leading: true, trailing: true })
  );
  const stopWindowMs$ = onStopWindowMs$.pipe(
    distinctUntilChanged(),
    throttleTime(50, asyncScheduler, { leading: true, trailing: true })
  );
  const collection$ = onList$.pipe(distinctUntilChanged());
  const timeRange$ = onTimeRange$.pipe(distinctUntilChanged());
  const height$ = onHeight$.pipe(distinctUntilChanged());

  /**
   * Redraw when Observables change.
   */
  const subscription = combineLatest(
    [
      startWindowMs$,
      stopWindowMs$,
      collection$,
      timeRange$,
      height$,
      onResize$.pipe(tap((width) => stage.width(width))),
    ],
    (startMs, stopMs, list, timeRange, height, width) => ({
      startMs,
      stopMs,
      list,
      timeRange,
      height,
      width,
    })
  )
    .pipe(takeUntil(onDestroy$), sample(raf$), mapRender(layer))
    .subscribe((l) => l.batchDraw());

  return {
    render: (
      { startWindowMs, stopWindowMs, list }: BackgroundBlurPropTypes,
      timeRange: BackgroundBlurSlice,
      height: number
    ) => {
      onStartWindowMs$.next(startWindowMs);
      onStopWindowMs$.next(stopWindowMs);
      if (list) {
        onList$.next(list);
      }
      if (timeRange) {
        onTimeRange$.next(timeRange);
      }
      if (height) {
        onHeight$.next(height);
      }
    },
    destroy: () => {
      subscription.unsubscribe();
      onDestroy$.next(true);
      stage.destroy();
    },
  };
};
